/// <amd-module name="Build/ApprovalsPanel/ChecksListPanel" />
import * as React from "react";

import { ApprovalOptionsGroup, ApprovalPanel } from "Build/ApprovalsPanel/ApprovalPanel";
import { IChecksPanelProps, ReviewMode } from "Build/ApprovalsPanel/ChecksListPanel.props";
import { IChecksPanelService } from "Build/ApprovalsPanel/ChecksPanelService.types";
import { LogsViewPanel } from "Build/ApprovalsPanel/LogsViewPanel";
import { ManualValidationOptionsGroup, ManualValidationPanel } from "Build/ApprovalsPanel/ManualValidationPanel";
import { getBuildUrl, getResourceUrl } from "Build/ApprovalsPanel/ResourceLinks";
import * as Resources from "Build/ApprovalsPanel/Resources";
import * as Utilities from "Build/ApprovalsPanel/Utilities";
import { ApprovalPermissions, ApprovalStatus, ApprovalUpdateParameters } from "Build/Client/Contracts/Approval";
import {
    ApprovalDetails,
    ApprovalStep,
    CheckDetails,
    CheckListItemType,
    ChecksPanelData,
    ChecksPermission,
    ExclusiveLockDetails,
    ManualValidationDetails,
    TaskCheckDetails
} from "Build/Client/Contracts/DataProvider";
import { ResourcePipelinePermissions } from "Build/Client/Contracts/PipelinePermissions";
import { CheckRunStatus } from "Build/Client/Contracts/PipelinesChecks";
import { IConfirmPipelineActionProps } from "Build/Common/Commands/ConfirmPipelineAction/Command.types";
import { CommandComponent } from "Build/Common/Library/Components/CommandComponent";

import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListItemDetails, IListRow, List, ListItem } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { Status, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { EvaluationOrder, MainApprovalDefTypeId, PostApprovalDefTypeId, PreApprovalDefTypeId } from "Build/ApprovalsPanel/Constants";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals, format, localeFormat } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssLayoutManager, useComponentContext } from "VSS/Platform/Layout";

import { css } from "VSSUI/Util";

const defaultStageName = "__default";

const ChecksPanel: React.FunctionComponent<IChecksPanelProps> = (props) => {
    const context = useComponentContext();
    const service = context.pageContext.getService<IChecksPanelService>("IChecksPanelService");
    service.initialize();
    service.fetchData(
        props.runId,
        props.stages.map((s) => s.id),
        props.checkListItemType
    );

    return <ChecksListPanelView {...props} />;
};

export const ChecksListPanelView: React.FC<IChecksPanelProps> = (props) => {
    const [approvalPanelVisible, setApprovalPanelVisible] = React.useState(false);
    const [manualValidationPanelVisible, setManualValidationPanelVisible] = React.useState(false);
    const [selectedApproval, setSelectedApproval] = React.useState<ApprovalDetails | undefined>(undefined);
    const [selectedManualValidation, setSelectedManualValidation] = React.useState<ManualValidationDetails | undefined>(undefined);
    const [selectedStage, setSelectedStage] = React.useState<string | undefined>(undefined);
    const [logsViewPanelVisible, setLogsViewPanelVisible] = React.useState(false);
    const [selectedTaskCheck, setSelectedTaskCheck] = React.useState<TaskCheckDetails | undefined>(undefined);
    const [showOverlay, setShowOverlay] = React.useState<boolean>(false);
    const [panelDescription, setPanelDescription] = React.useState<string>("");
    const [arePendingChecksPresent, setArePendingChecksPresent] = React.useState<boolean>(false);
    const [projectId, setProjectId] = React.useState<string>("");

    const context = props.componentContext || useComponentContext();
    const service = context.pageContext.getService<IChecksPanelService>("IChecksPanelService");
    const showOrderedChecks = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableOrderingChecks", false);

    const onSelectApproval = (approval: ApprovalDetails, stageName: string) => {
        setSelectedApproval(approval);
        setSelectedStage(stageName);
        setApprovalPanelVisible(true);
    };

    const onSelectManualValidation = (manualValidation: ManualValidationDetails, stageName: string) => {
        setSelectedManualValidation(manualValidation);
        setSelectedStage(stageName);
        setManualValidationPanelVisible(true);
    };

    const onSelectTaskCheck = (taskCheck: TaskCheckDetails, arePendingChecksPresentInStage: boolean) => {
        setLogsViewPanelVisible(true);
        setSelectedTaskCheck(taskCheck);
        setArePendingChecksPresent(arePendingChecksPresentInStage);
    };

    const onApprovalPanelBackClick = (event: React.SyntheticEvent<HTMLElement>) => {
        setSelectedApproval(undefined);
        setApprovalPanelVisible(false);
    };

    const onManualValidationPanelBackClick = (event: React.SyntheticEvent<HTMLElement>) => {
        setSelectedManualValidation(undefined);
        setManualValidationPanelVisible(false);
    };

    const onLogsViewPanelBackClick = (event: React.SyntheticEvent<HTMLElement>) => {
        setSelectedTaskCheck(undefined);
        setLogsViewPanelVisible(false);
    };

    let panelTitle = Resources.DefaultPanelTitle;
    if (props.reviewMode === ReviewMode.AllPending) {
        panelTitle = Resources.AllPendingPanelTitle;
    } else if (props.reviewMode === ReviewMode.Stage && props.stages && props.stages[0] && props.stages[0].name !== defaultStageName) {
        switch (props.checkListItemType) {
            case CheckListItemType.All:
                panelTitle = format(Resources.StagePanelTitleCommon, props.stages[0].name);
                break;
            case CheckListItemType.Checks:
                panelTitle = format(Resources.StagePanelTitleChecks, props.stages[0].name);
                break;
            case CheckListItemType.ManualValidation:
                panelTitle = format(Resources.StagePanelTitleManualValidation, props.stages[0].name);
                break;
        }
    }

    const isDialogOpen = new ObservableValue<boolean>(false);
    const dialogData = new ObservableValue<ICommentDialogData>({ status: ApprovalStatus.Approved, approvals: [] });

    const dismissDialog = () => {
        isDialogOpen.value = false;
    };

    const openDialog = (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[]) => {
        isDialogOpen.value = true;
        dialogData.value = {
            status: status,
            approvals: approvals
        };
    };

    const submitApprovals = (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[], message: string, deferredTo: Date | null) => {
        const updates = (approvals as Array<ApprovalDetails | ManualValidationDetails>).map((a) => {
            return {
                approvalId: a.id,
                status: status,
                comment: message || "",
                deferredTo: deferredTo
            } as ApprovalUpdateParameters;
        });
        setShowOverlay(true);
        props.onSubmitBulkApproval(updates);
    };

    const reassignStep = (approval: ApprovalDetails, step: ApprovalStep, reassignTo: IdentityRef, message: string): Promise<void> => {
        let updates: ApprovalUpdateParameters[] = new Array<ApprovalUpdateParameters>();

        let reassignBody = {
            approvalId: approval.id,
            assignedApprover: step.assignedApprover,
            reassignTo: reassignTo,
            comment: message || ""
        } as ApprovalUpdateParameters;
        updates.push(reassignBody);

        return props.onReassignApprovals(updates);
    };

    return (
        <Panel
            onDismiss={props.onDismiss}
            titleProps={{ text: panelTitle }}
            description={panelDescription}
            overlayContent={showOverlay ? <Spinner /> : undefined}
        >
            <Observer stages={service.data}>
                {(observerProps: { stages: ChecksPanelData[] }) => {
                    const stagesWithChecks: ChecksPanelData[] = [];
                    let hasNonApprovalChecks = false;

                    // Approvals
                    const myApprovalStages = observerProps.stages.filter((stage) => {
                        return stage.approvals && stage.approvals.some((a) => Utilities.isCheckPermissionUpdate(a.permissions));
                    });
                    const otherApprovalStages = observerProps.stages.filter((stage) => {
                        return stage.approvals && stage.approvals.length > 0 && stage.approvals.every((a) => a.permissions === ChecksPermission.View);
                    });
                    const sortedApprovalStages = myApprovalStages.concat(otherApprovalStages);
                    sortedApprovalStages.forEach((x) => stagesWithChecks.push(x));

                    // ManualValidations
                    const myManualValidationStages = observerProps.stages.filter((stage) => {
                        return stage.manualValidations && stage.manualValidations.some((a) => Utilities.isCheckPermissionUpdate(a.permissions));
                    });
                    const otherManualValidationStages = observerProps.stages.filter((stage) => {
                        return (
                            stage.manualValidations &&
                            stage.manualValidations.length > 0 &&
                            stage.manualValidations.every((a) => a.permissions === ChecksPermission.View)
                        );
                    });
                    const sortedManualValidationStages = myManualValidationStages.concat(otherManualValidationStages);

                    //Check if stage is already added due to approvals
                    for (let stage of sortedManualValidationStages) {
                        const hasApprovals = stage.approvals && stage.approvals.length > 0;
                        if (!hasApprovals) {
                            stagesWithChecks.push(stage);
                        }
                    }

                    // Add other types of checks
                    for (let stage of observerProps.stages) {
                        const hasApprovals = stage.approvals && stage.approvals.length > 0;
                        const hasManualValidations = stage.manualValidations && stage.manualValidations.length > 0;
                        const hasTaskChecks = stage.taskChecks && stage.taskChecks.length > 0;
                        const hasExclusiveLockChecks = stage.exclusiveLockChecks && stage.exclusiveLockChecks.length > 0;
                        const hasNonAuthOtherChecks =
                            stage.checks && stage.checks.filter((x) => !equals(x.typeId, Utilities.AuthorizationCheckTypeIdString, true)).length > 0;
                        if (hasTaskChecks || hasExclusiveLockChecks || hasNonAuthOtherChecks) {
                            if (!hasApprovals && !hasManualValidations) {
                                stagesWithChecks.push(stage);
                            }
                            hasNonApprovalChecks = true;
                        }
                    }

                    setProjectId(observerProps.stages && observerProps.stages.length > 0 ? observerProps.stages[0].projectId : "");

                    // permissions
                    const stagesWithPermissionChecks = observerProps.stages.filter(
                        (stage) =>
                            stage.checks &&
                            stage.checks.length > 0 &&
                            stage.checks.every((c) => equals(c.typeId, Utilities.AuthorizationCheckTypeIdString, true))
                    );
                    const myPermissionStages = stagesWithPermissionChecks.filter((stage) =>
                        stage.checks.some((c) => Utilities.isCheckPermissionUpdate(c.permissions))
                    );
                    const otherPermissionStages = stagesWithPermissionChecks.filter(
                        (stage) => stage.checks.length > 0 && stage.checks.every((c) => c.permissions === ChecksPermission.View)
                    );
                    const sortedPermissionStages = myPermissionStages.concat(otherPermissionStages);

                    const bypassChecksPanel = shouldBypassChecksPanel(stagesWithChecks, sortedPermissionStages, hasNonApprovalChecks);

                    if (stagesWithChecks.length === 1) {
                        let { stageTimeoutMessage, stageTimeoutMessageToolTip } = getStageTimeoutDetails(stagesWithChecks[0]);
                        setPanelDescription(stageTimeoutMessage!);
                    }

                    return (
                        <div className="flex-column rhythm-vertical-16 pipelines-checks-container">
                            {!bypassChecksPanel &&
                                stagesWithChecks.map((stageCheckData) => {
                                    return (showOrderedChecks 
                                        ? 
                                        <ChecksStageGroupOrdered
                                            stageData={stageCheckData}
                                            key={"StageGroup" + stageCheckData.stageId}
                                            onSelectTaskCheck={onSelectTaskCheck}
                                            onSelectApproval={onSelectApproval}
                                            onSelectManualValidation={onSelectManualValidation}
                                            reviewMode={props.reviewMode}
                                            submitApprovals={submitApprovals}
                                            openReviewDialog={openDialog}
                                            isOrderedChecksEnabled={showOrderedChecks}
                                        /> 
                                        :
                                        <ChecksStageGroup
                                            stageData={stageCheckData}
                                            key={"StageGroup" + stageCheckData.stageId}
                                            onSelectTaskCheck={onSelectTaskCheck}
                                            onSelectApproval={onSelectApproval}
                                            onSelectManualValidation={onSelectManualValidation}
                                            reviewMode={props.reviewMode}
                                            submitApprovals={submitApprovals}
                                            openReviewDialog={openDialog}
                                            isOrderedChecksEnabled={showOrderedChecks}
                                        />
                                    );
                                })}
                            {sortedPermissionStages.map((checksDataForStage) => {
                                return (
                                    checksDataForStage.checks.length > 0 && (
                                        <PermissionStageGroup
                                            key={"StageGroup" + checksDataForStage.stageId}
                                            pipelineId={props.pipelineId}
                                            stageData={checksDataForStage}
                                            onPermitResource={props.onPermitResource}
                                            reviewMode={props.reviewMode}
                                        />
                                    )
                                );
                            })}
                            {bypassChecksPanel && stagesWithChecks[0].approvals && (
                                <ApprovalPanel
                                    approval={stagesWithChecks[0].approvals[0]}
                                    stageName={stagesWithChecks[0].stageName}
                                    onBackClick={props.onDismiss}
                                    onDismiss={props.onDismiss}
                                    onSubmitApprovals={submitApprovals}
                                    onReassignStep={reassignStep}
                                />
                            )}
                            {bypassChecksPanel && stagesWithChecks[0].manualValidations && (
                                <ManualValidationPanel
                                    manualValidation={stagesWithChecks[0].manualValidations[0]}
                                    stageName={stagesWithChecks[0].stageName}
                                    onBackClick={props.onDismiss}
                                    onDismiss={props.onDismiss}
                                    onSubmitManualValidations={submitApprovals}
                                />
                            )}

                            {bypassChecksPanel && stagesWithChecks[0].taskChecks && (
                                <LogsViewPanel
                                    check={stagesWithChecks[0].taskChecks[0]}
                                    arePendingChecksPresent={stagesWithChecks[0].taskChecks[0].status === CheckRunStatus.Running}
                                    projectId={projectId}
                                    onBackClick={props.onDismiss}
                                    onDismiss={props.onDismiss}
                                />
                            )}
                        </div>
                    );
                }}
            </Observer>
            {approvalPanelVisible && (
                <ApprovalPanel
                    approval={selectedApproval!}
                    stageName={selectedStage!}
                    onBackClick={onApprovalPanelBackClick}
                    onDismiss={props.onDismiss}
                    onSubmitApprovals={submitApprovals}
                    onReassignStep={reassignStep}
                />
            )}
            {manualValidationPanelVisible && (
                <ManualValidationPanel
                    manualValidation={selectedManualValidation!}
                    stageName={selectedStage!}
                    onBackClick={onManualValidationPanelBackClick}
                    onDismiss={props.onDismiss}
                    onSubmitManualValidations={submitApprovals}
                />
            )}
            {logsViewPanelVisible && (
                <LogsViewPanel
                    check={selectedTaskCheck!}
                    arePendingChecksPresent={arePendingChecksPresent}
                    projectId={projectId}
                    onBackClick={onLogsViewPanelBackClick}
                    onDismiss={props.onDismiss}
                />
            )}
        </Panel>
    );
};

interface IPermissionStageGroup {
    pipelineId: number;
    stageData: ChecksPanelData;
    onPermitResource: (permissionsRequest: ResourcePipelinePermissions, resourceType: string, resourceId: string) => void;
    reviewMode: ReviewMode;
}

const PermissionStageGroup: React.SFC<IPermissionStageGroup> = (props) => {
    const permissions = new ArrayItemProvider<CheckDetails>(props.stageData.checks);
    const stageName = props.stageData.stageName;
    const context = useComponentContext();
    const shouldShowStage = props.reviewMode === ReviewMode.AllPending && stageName !== defaultStageName;

    const permitResource = (check: CheckDetails) => {
        const permissions = {
            pipelines: [
                {
                    id: props.pipelineId,
                    authorized: true
                }
            ]
        } as ResourcePipelinePermissions;

        const resourceType = check.resource.type;

        const onConfirm = () => {
            props.onPermitResource(permissions, resourceType, check.resource.id);
        };

        const layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            const props: IConfirmPipelineActionProps = {
                title: Resources.PermitConfirmDialogTitle,
                message: localeFormat(Resources.PermitConfirmDialogDescription, Utilities.getResourceTypeString(resourceType), check.resource.name),
                onClose: onDismiss,
                cancelLabel: Resources.Cancel,
                onConfirm: onConfirm,
                confirmLabel: Resources.Permit
            };

            return React.createElement(
                CommandComponent,
                {
                    commandType: "ciDConfirmPipelineComponent",
                    ...props
                },
                null
            );
        });
    };

    return (
        <div>
            {shouldShowStage && (
                <div className="flex-row body-m checks-group-header">
                    <div className="flex-column flex-grow justify-end">
                        <div className="flex-row font-weight-semibold checks-group-header-row">
                            <span>{props.stageData.stageName}</span>
                        </div>
                    </div>
                </div>
            )}

            <List
                className="pipelines-checks-container"
                itemProvider={permissions}
                renderRow={(index: number, check: CheckDetails, details: IListItemDetails<CheckDetails>) => {
                    const resourceName = check.resource.name;
                    const permitButtonId = format("pm-{0}-{1}-{2}", props.stageData.stageId, check.resource.type, check.resource.id);
                    const resourceIconName = check.resource ? Utilities.getResourceTypeIcons(check.resource.type) : "Server";
                    const resourceType = check.resource ? Utilities.getResourceTypeString(check.resource.type) : "";

                    return (
                        <ListItem key={"ci-" + index} index={index} details={details}>
                            <div className="flex-row flex-grow flex-center h-scroll-hidden">
                                <Icon className="pipelines-resource-icon" iconName="Lock" />
                                <div className="pipelines-approval-row-text flex-column flex-grow h-scroll-hidden">
                                    <span className="body-m text-ellipsis">{Resources.Permission}</span>
                                    <span className="body-s text-ellipsis secondary-text">{Resources.PermissionSecondary}</span>
                                </div>
                                <Icon className="pipelines-resource-icon" iconName={resourceIconName} />
                                <div className="pipelines-approval-row-text flex-column flex-grow h-scroll-hidden">
                                    <Link className="pipelines-resource-link" href={getResourceUrl(context.pageContext, check.resource)}>
                                        {resourceName}
                                    </Link>
                                    <span className="body-s text-ellipsis secondary-text">{resourceType}</span>
                                </div>
                                {Utilities.isCheckPermissionUpdate(check.permissions) && (
                                    <ButtonGroup className="pipelines-permission-button">
                                        <Button id={permitButtonId} primary={true} text={Resources.Permit} onClick={() => permitResource(check)} />
                                    </ButtonGroup>
                                )}
                            </div>
                        </ListItem>
                    );
                }}
            />
        </div>
    );
};

interface ICommentDialogData {
    status: ApprovalStatus;
    approvals: ApprovalDetails[] | ManualValidationDetails[];
}

interface ICheckStageGroupProps {
    reviewMode: ReviewMode;
    stageData: ChecksPanelData;
    isOrderedChecksEnabled: boolean;
    onSelectApproval: (approval: ApprovalDetails, name: string) => void;
    onSelectManualValidation: (manualValidation: ManualValidationDetails, name: string) => void;
    onSelectTaskCheck: (taskCheck: TaskCheckDetails, arePendingChecksPresentInStage: boolean) => void;
    submitApprovals: (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[], message?: string, deferredTo?: Date | null) => void;
    openReviewDialog: (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[]) => void;
}

const ChecksStageGroup: React.SFC<ICheckStageGroupProps> = (props) => {
    let isUserBlockedFromApproving = props.stageData.approvals && isCurrentUserBlockedInAnyApproval(props.stageData.approvals);

    //To eliminate those approvals which are in sequence and approver is waiting on sequence.
    const isApprovalAllowed = (approval: ApprovalDetails): boolean => {
        let allowedApprovals = approval.steps.filter((a) => a.status === ApprovalStatus.Pending && a.permissions === ApprovalPermissions.Update);
        return allowedApprovals.length > 0;
    };
    const myApprovals = props.stageData.approvals
        ? new ObservableArray<ApprovalDetails>(
              props.stageData.approvals.filter((a) => Utilities.isCheckPermissionUpdate(a.permissions) && isApprovalAllowed(a))
          )
        : new ObservableArray([]);
    const otherApprovals = props.stageData.approvals
        ? new ObservableArray<ApprovalDetails>(getOtherApprovals(props.stageData.approvals))
        : new ObservableArray([]);
    const myManualValidations = props.stageData.manualValidations
        ? new ObservableArray<ManualValidationDetails>(props.stageData.manualValidations.filter((a) => Utilities.isCheckPermissionUpdate(a.permissions)))
        : new ObservableArray([]);
    const otherManualValidations = props.stageData.manualValidations
        ? new ObservableArray<ManualValidationDetails>(getOtherManualValidations(props.stageData.manualValidations))
        : new ObservableArray([]);
    const taskChecks = props.stageData.taskChecks ? new ObservableArray<TaskCheckDetails>(props.stageData.taskChecks) : new ObservableArray([]);
    const extendsChecksItems = props.stageData.checks
        ? props.stageData.checks.filter((c) => equals(c.typeId, Utilities.ExtendsCheckTypeIdString, true))
        : null;
    const extendsChecks = extendsChecksItems ? new ObservableArray<CheckDetails>(extendsChecksItems) : new ObservableArray([]);
    const exclusiveLockChecks = props.stageData.exclusiveLockChecks
        ? new ObservableArray<ExclusiveLockDetails>(props.stageData.exclusiveLockChecks)
        : new ObservableArray([]);

    const arePendingChecksPresentInStage = arePendingChecksPresent(props.stageData);

    const { stageTimeoutMessage, stageTimeoutMessageToolTip } = getStageTimeoutDetails(props.stageData);

    const onSubmitReview = (status: ApprovalStatus, commentMessage: string, deferredTo: Date | null, isManualValidation: boolean = false) => {
        if (isManualValidation) {
            props.submitApprovals(status, myManualValidations.value, commentMessage);
        } else {
            props.submitApprovals(status, myApprovals.value, commentMessage, deferredTo);
        }
    };

    const openReviewDialog = (status: ApprovalStatus) => {
        props.openReviewDialog(status, myApprovals.value);
    };

    return (
        <div>
            <StageGroupHeader
                myApprovalsCount={myApprovals.length}
                myManualValidationsCount={myManualValidations.length}
                otherChecksCount={otherApprovals.length + otherManualValidations.length}
                stageName={props.stageData.stageName}
                stageTimeoutString={stageTimeoutMessage!}
                stageTimeoutStringToolTip={stageTimeoutMessageToolTip!}
                reviewMode={props.reviewMode}
                openReviewDialog={openReviewDialog}
            />
            {myApprovals.length > 0 && (
                <ApprovalOptionsGroup isApprovalInSequence={false} myApprovalsCount={myApprovals.length} onSubmit={onSubmitReview} />
            )}
            {myApprovals.length > 0 && (
                <ApprovalGroup 
                    itemProvider={myApprovals} 
                    onSelectApproval={props.onSelectApproval} 
                    stageName={props.stageData.stageName} 
                    isOrderedChecksEnabled={props.isOrderedChecksEnabled}/>
            )}
            {isUserBlockedFromApproving && (
                <MessageCard className="requester-blocked-message-card" severity={MessageCardSeverity.Warning}>
                    {Resources.CurrentUserCannotApproveMessage}
                </MessageCard>
            )}
            {otherApprovals.length > 0 && (
                <ApprovalGroup
                    headerText={Resources.Others}
                    itemProvider={otherApprovals}
                    onSelectApproval={props.onSelectApproval}
                    hideHeader={myApprovals.length === 0}
                    stageName={props.stageData.stageName}
                    isOrderedChecksEnabled={props.isOrderedChecksEnabled}
                />
            )}
            {taskChecks.length > 0 && (
                <ChecksGroup
                    headerText={Resources.Others}
                    checks={taskChecks}
                    onSelectCheck={props.onSelectTaskCheck}
                    arePendingChecksPresent={arePendingChecksPresentInStage}
                    hideHeader={myApprovals.length === 0 || otherApprovals.length > 0}
                />
            )}
            {extendsChecks.length > 0 && (
                <ExtendsGroup
                    headerText={Resources.Others}
                    checks={extendsChecks}
                    hideHeader={myApprovals.length === 0 || otherApprovals.length > 0}
                />
            )}
            {exclusiveLockChecks.length > 0 && (
                <ExclusiveLockGroup
                    headerText={Resources.Others}
                    checks={exclusiveLockChecks}
                    hideHeader={myApprovals.length === 0 || otherApprovals.length > 0}
                />
            )}
            {myManualValidations.length > 0 && (
                <ManualValidationOptionsGroup myManualValidationsCount={myManualValidations.length} onSubmit={onSubmitReview} />
            )}
            {myManualValidations.length > 0 && (
                <ApprovalGroup
                    itemProvider={myManualValidations}
                    onSelectApproval={props.onSelectManualValidation}
                    stageName={props.stageData.stageName}
                    isOrderedChecksEnabled={props.isOrderedChecksEnabled}
                />
            )}
            {otherManualValidations.length > 0 && (
                <ApprovalGroup
                    headerText={Resources.Others}
                    itemProvider={otherManualValidations}
                    onSelectApproval={props.onSelectManualValidation}
                    hideHeader={myManualValidations.length === 0}
                    stageName={props.stageData.stageName}
                    isOrderedChecksEnabled={props.isOrderedChecksEnabled}
                />
            )}
        </div>
    );
};


interface IChecksStageGroupOrdered {   
    reviewMode: ReviewMode;
    stageData: ChecksPanelData;
    isOrderedChecksEnabled: boolean;
    onSelectApproval: (approval: ApprovalDetails, name: string) => void;
    onSelectManualValidation: (manualValidation: ManualValidationDetails, name: string) => void;
    onSelectTaskCheck: (taskCheck: TaskCheckDetails, arePendingChecksPresentInStage: boolean) => void;
    submitApprovals: (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[], message?: string, deferredTo?: Date | null) => void;
    openReviewDialog: (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[]) => void;
}

export const ChecksStageGroupOrdered: React.SFC<IChecksStageGroupOrdered> = (props) => {    
    // Groups checks by evaluation order
    const groupByEvaluationOrder = <T extends CheckDetails>(checks:  T[]) => {
        return checks.reduce((result, check) =>  {
            var evaluationOrderList = result.get(check.evaluationOrder) || [];
            evaluationOrderList.push(check);
            result.set(check.evaluationOrder, evaluationOrderList);
            return result;
        }, new Map<EvaluationOrder, T[]>());
    }

    //To eliminate those approvals which are in sequence and approver is waiting on sequence.
    const isApprovalAllowed = (approval: ApprovalDetails): boolean => {
        let allowedApprovals = approval.steps.filter((a) => a.status === ApprovalStatus.Pending && a.permissions === ApprovalPermissions.Update);
        return allowedApprovals.length > 0;
    };
        
    const openReviewDialog = (status: ApprovalStatus, myApprovals: ApprovalDetails[]) => {
        props.openReviewDialog(status, myApprovals);
    }; 
    
    // Lists of all check types in stage.
    const myApprovals = props.stageData.approvals ? props.stageData.approvals.filter((a) => Utilities.isCheckPermissionUpdate(a.permissions) && isApprovalAllowed(a)) : [];
    const otherApprovals = props.stageData.approvals ? getOtherApprovals(props.stageData.approvals) : [];
    const myManualValidations = props.stageData.manualValidations ? props.stageData.manualValidations.filter((a) => Utilities.isCheckPermissionUpdate(a.permissions)): [];
    const otherManualValidations = props.stageData.manualValidations ? getOtherManualValidations(props.stageData.manualValidations): [];
    const taskChecks = props.stageData.taskChecks || []
    const extendsChecks = props.stageData.checks ? props.stageData.checks.filter((c) => equals(c.typeId, Utilities.ExtendsCheckTypeIdString, true)) : [];
    const exclusiveLockChecks = props.stageData.exclusiveLockChecks || [];

    // Groups of all check types in stage grouped by evaluationOrder
    const myApprovalsGrouped = groupByEvaluationOrder(myApprovals);
    const otherApprovalsGrouped = groupByEvaluationOrder(otherApprovals);
    const myManualValidationsGrouped = groupByEvaluationOrder(myManualValidations);
    const otherManualValidationsGrouped = groupByEvaluationOrder(otherManualValidations);
    const taskChecksGrouped = groupByEvaluationOrder(taskChecks);
    const extendsChecksGrouped = groupByEvaluationOrder(extendsChecks);
    const exclusiveLockChecksGrouped = groupByEvaluationOrder(exclusiveLockChecks);

    const { stageTimeoutMessage, stageTimeoutMessageToolTip } = getStageTimeoutDetails(props.stageData);
    

    const evaluationOrderPhases = Object.values(EvaluationOrder)
        .filter(key => Number.isInteger(Number(key)));

    let isFirstEvaluationPhase = true 
    const evaluationPhases = evaluationOrderPhases.map((key) => {
            const phase = key as number;
            var myApprovalsPhase = myApprovalsGrouped.get(phase) || [];
            var myManualValidationsPhase = myManualValidationsGrouped.get(phase) || [];
            var otherApprovalsPhase = otherApprovalsGrouped.get(phase) || [];
            var otherManualValidationsPhase = otherManualValidationsGrouped.get(phase) || [];
            var taskChecksPhase = taskChecksGrouped.get(phase) || [];
            var extendsChecksPhase = extendsChecksGrouped.get(phase) || [];
            var exclusiveLockChecksPhase = exclusiveLockChecksGrouped.get(phase) || [];
            
            if(myApprovalsPhase.length <= 0 
                && myManualValidationsPhase.length <= 0 
                && otherApprovalsPhase.length <= 0 
                && otherManualValidationsPhase.length <= 0 
                && taskChecksPhase.length <= 0 
                && extendsChecksPhase.length <= 0 
                && exclusiveLockChecksPhase.length <= 0) {
                return;
            }
            
            let renderSeperator = true
            if(isFirstEvaluationPhase) {
                renderSeperator = false;
                isFirstEvaluationPhase = false;
            }

            return <ChecksStageOrdered 
                        {...props}
                        evaluationOrder={phase}
                        myApprovals={new ObservableArray(myApprovalsPhase)}
                        myManualValidations={new ObservableArray(myManualValidationsPhase)}
                        otherApprovals={new ObservableArray(otherApprovalsPhase)}
                        otherManualValidations={new ObservableArray(otherManualValidationsPhase)}
                        taskChecks={new ObservableArray(taskChecksPhase)}
                        extendsChecks={new ObservableArray(extendsChecksPhase)}
                        exclusiveLockChecks={new ObservableArray(exclusiveLockChecksPhase)}
                        renderSeperator={renderSeperator}
                        key={"phase_" + phase}
                    />;
        });

    return ( 
        <div>
            <StageGroupHeader
                myApprovalsCount={myApprovals.length}
                myManualValidationsCount={myManualValidations.length}
                otherChecksCount={otherApprovals.length + otherManualValidations.length}
                stageName={props.stageData.stageName}
                stageTimeoutString={stageTimeoutMessage!}
                stageTimeoutStringToolTip={stageTimeoutMessageToolTip!}
                reviewMode={props.reviewMode}
                openReviewDialog={(status: ApprovalStatus) => openReviewDialog(status, myApprovals)}
            />
            {evaluationPhases}
        </div>
    );
}

interface IChecksStageOrderedProps {
    stageData: ChecksPanelData;
    showCheckIssues?: boolean;
    evaluationOrder: number;
    onSelectApproval: (approval: ApprovalDetails, name: string) => void;
    onSelectManualValidation: (manualValidation: ManualValidationDetails, name: string) => void;
    onSelectTaskCheck: (taskCheck: TaskCheckDetails, arePendingChecksPresentInStage: boolean) => void;
    submitApprovals: (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[], message?: string, deferredTo?: Date| null) => void;
    renderSeperator: boolean;
    isOrderedChecksEnabled: boolean;

    myApprovals: ObservableArray<ApprovalDetails>;
    myManualValidations: ObservableArray<ManualValidationDetails>;
    otherApprovals: ObservableArray<ApprovalDetails>;
    otherManualValidations: ObservableArray<ManualValidationDetails>;
    taskChecks: ObservableArray<TaskCheckDetails>;
    extendsChecks: ObservableArray<CheckDetails>;
    exclusiveLockChecks: ObservableArray<ExclusiveLockDetails>;
}

const ChecksStageOrdered: React.SFC<IChecksStageOrderedProps> = (props) => {
    const isUserBlockedFromApproving = props.stageData.approvals && isCurrentUserBlockedInAnyApproval(props.stageData.approvals);
    const arePendingChecksPresentInStage = arePendingChecksPresent(props.stageData);

    const onSubmitReview = (status: ApprovalStatus, commentMessage: string, deferredTo: Date | null, isManualValidation: boolean = false) => {
        if (isManualValidation) {
            props.submitApprovals(status, props.myManualValidations.value, commentMessage);
        } else {
            props.submitApprovals(status, props.myApprovals.value, commentMessage, deferredTo);
        }
    };
    
    return (
        <div className={css(props.renderSeperator && "stage-checks-ordered")}>
            {props.myApprovals.length > 0 && (
                <ApprovalOptionsGroup isApprovalInSequence={false} myApprovalsCount={props.myApprovals.length} onSubmit={onSubmitReview} />
            )}
            {props.myApprovals.length > 0 && (
                <ApprovalGroup 
                    itemProvider={props.myApprovals} 
                    onSelectApproval={props.onSelectApproval} 
                    stageName={props.stageData.stageName} 
                    isOrderedChecksEnabled={props.isOrderedChecksEnabled}/>
            )}
            {isUserBlockedFromApproving && (
                <MessageCard className="requester-blocked-message-card" severity={MessageCardSeverity.Warning}>
                    {Resources.CurrentUserCannotApproveMessage}
                </MessageCard>
            )}
            {props.otherApprovals.length > 0 && (
                <ApprovalGroup
                    headerText={Resources.Others}
                    itemProvider={props.otherApprovals}
                    onSelectApproval={props.onSelectApproval}
                    hideHeader={props.myApprovals.length === 0}
                    stageName={props.stageData.stageName}
                    isOrderedChecksEnabled={props.isOrderedChecksEnabled}
                />
            )}
            {props.taskChecks.length > 0 && (
                <ChecksGroup
                    headerText={Resources.Others}
                    checks={props.taskChecks}
                    onSelectCheck={props.onSelectTaskCheck}
                    arePendingChecksPresent={arePendingChecksPresentInStage}
                    hideHeader={props.myApprovals.length === 0 || props.otherApprovals.length > 0}
                />
            )}
            {props.extendsChecks.length > 0 && (
                <ExtendsGroup
                    headerText={Resources.Others}
                    checks={props.extendsChecks}
                    hideHeader={props.myApprovals.length === 0 || props.otherApprovals.length > 0}
                />
            )}
            {props.exclusiveLockChecks.length > 0 && (
                <ExclusiveLockGroup
                    headerText={Resources.Others}
                    checks={props.exclusiveLockChecks}
                    hideHeader={props.myApprovals.length === 0 || props.otherApprovals.length > 0}
                />
            )}
            {props.myManualValidations.length > 0 && (
                <ManualValidationOptionsGroup myManualValidationsCount={props.myManualValidations.length} onSubmit={onSubmitReview} />
            )}
            {props.myManualValidations.length > 0 && (
                <ApprovalGroup
                    itemProvider={props.myManualValidations}
                    onSelectApproval={props.onSelectManualValidation}
                    isOrderedChecksEnabled={props.isOrderedChecksEnabled}
                    stageName={props.stageData.stageName}
                />
            )}
            {props.otherManualValidations.length > 0 && (
                <ApprovalGroup
                    headerText={Resources.Others}
                    itemProvider={props.otherManualValidations}
                    onSelectApproval={props.onSelectManualValidation}
                    hideHeader={props.myManualValidations.length === 0}
                    isOrderedChecksEnabled={props.isOrderedChecksEnabled}
                    stageName={props.stageData.stageName}
                />
            )}
        </div>
    );
};

interface IApprovalGroupProps {
    itemProvider: ObservableArray<ApprovalDetails> | ObservableArray<ManualValidationDetails>;
    stageName: string;
    onSelectApproval: (approval: ApprovalDetails | ManualValidationDetails, stageName: string) => void;
    isOrderedChecksEnabled: boolean;
    hideHeader?: boolean;
    headerText?: string;
}

const ApprovalGroup: React.SFC<IApprovalGroupProps> = (props) => {
    const approvalClickable =(item: ApprovalDetails): boolean => {
        return !props.isOrderedChecksEnabled || 
        (item.status !== CheckRunStatus.Canceled 
            && item.status !== CheckRunStatus.TimedOut 
            && item.status !== CheckRunStatus.Queued 
            && item.status !== CheckRunStatus.None
        )
    }

    const onListItemActivate = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<ApprovalDetails> | IListRow<ManualValidationDetails>) => {
        if (!event.defaultPrevented) {
            props.onSelectApproval(listRow.data, props.stageName);
        }
    };
    const renderApproval = (
        rowIndex: number,
        item: ApprovalDetails | ManualValidationDetails,
        details: IListItemDetails<ApprovalDetails> | IListItemDetails<ManualValidationDetails>
    ) => {
        const approvalStatus = Utilities.checksToApprovalStatus(item.status);
        const statusIndicatorData = Utilities.getApprovalStatusIndicatorData(approvalStatus);
        const checkName = getCheckName(item);

         // Do a shallow copy as list props is read only object
         if(props.isOrderedChecksEnabled) {
            details.listProps = {
                ...details.listProps,
                singleClickActivation: approvalClickable(item)
            };
        }

        return <CheckListItem rowIndex={rowIndex} check={item} details={details} checkName={checkName} statusIndicator={statusIndicatorData} />;
    };

    return (
        <div>
            {!props.hideHeader && !!props.headerText && showHeader(props.headerText)}
            <List
                itemProvider={props.itemProvider}
                renderRow={renderApproval}
                singleClickActivation={true}
                width="100%"
                onActivate={onListItemActivate}
            />
        </div>
    );
};

interface IExtendsGroupProps {
    headerText: string;
    checks: ObservableArray<CheckDetails>;
    hideHeader: boolean;
}

const ExtendsGroup: React.SFC<IExtendsGroupProps> = (props) => {
    const renderChecks = (rowIndex: number, item: CheckDetails, details: IListItemDetails<CheckDetails>) => (
        <CheckListItem check={item} resultMessage={item.resultMessage} checkName={Resources.RequiredTemplate} details={details} rowIndex={rowIndex} />
    );

    return (
        <div>
            {!props.hideHeader && !!props.headerText && showHeader(props.headerText)}
            <List itemProvider={props.checks} renderRow={renderChecks} singleClickActivation={false} width="100%" />
        </div>
    );
};

interface IExclusiveLockGroupProps {
    headerText: string;
    checks: ObservableArray<ExclusiveLockDetails>;
    hideHeader: boolean;
}

const ExclusiveLockGroup: React.SFC<IExclusiveLockGroupProps> = (props) => {
    const context = useComponentContext();

    const renderChecks = (rowIndex: number, item: ExclusiveLockDetails, details: IListItemDetails<ExclusiveLockDetails>) => {
        const status = Utilities.getExclusiveLockStatusIndicatorData(item.status);
        let label: React.ReactChild = status.label;
        if (item.lockInUse && item.buildName && item.status == CheckRunStatus.Running) {
            // Custom label for linking back to the build with the lock
            label = (
                <div>
                    <span>{Resources.ExclusiveLockHeldBy + " "}</span>
                    <Link className="pipelines-resource-link" href={getBuildUrl(context.pageContext, item.projectId, item.buildId)}>
                        {item.buildName}
                    </Link>
                </div>
            );
        }
        return (
            <CheckListItem
                check={item}
                resultMessage={item.resultMessage}
                checkName={Resources.ExclusiveLock}
                details={details}
                rowIndex={rowIndex}
                statusIndicator={status}
                statusLabel={label}
            />
        );
    };

    return (
        <div>
            {!props.hideHeader && !!props.headerText && showHeader(props.headerText)}
            <List itemProvider={props.checks} renderRow={renderChecks} singleClickActivation={false} width="100%" />
        </div>
    );
};

interface ICheckListItemProps {
    check: any;
    arePendingChecksPresent?: boolean;
    resultMessage?: string;
    checkName: string;
    details: IListItemDetails<ApprovalDetails | TaskCheckDetails | CheckDetails | ManualValidationDetails>;
    rowIndex: number;
    statusIndicator?: Utilities.IStatusIndicatorData;
    statusLabel?: React.ReactChild;
}

const CheckListItem: React.SFC<ICheckListItemProps> = (props) => {
    const statusIndicatorData = props.statusIndicator || Utilities.getCheckRunStatusIndicatorData(props.check.status);
    const statusLabel = props.statusLabel || statusIndicatorData.label;
    const isManualValidation = equals(props.check.typeId, Utilities.ManualValidationTaskTypeId, true);

    if (isManualValidation) {
        const mvStatusIndicatorData = Utilities.getApprovalStatusIndicatorData(props.check.status, true);
        const mvStatusLabel = mvStatusIndicatorData.label;

        return (
            <div>
                <ListItem key={"manual-validation-item" + props.rowIndex} index={props.rowIndex} details={props.details}>
                    <div className="pipelines-approval-row flex-row flex-grow flex-center h-scroll-hidden">
                        <Status {...mvStatusIndicatorData.statusProps} size={StatusSize.m} />
                        <div className="pipelines-approval-text flex-column h-scroll-hidden">
                            <span className="body-m text-ellipsis">{props.checkName}</span>
                            <span className="body-s text-ellipsis secondary-text">{mvStatusLabel}</span>
                        </div>
                        <span className="body-s flex-column flex-grow font-weight-semibold h-scroll-hidden"> {props.check.jobName} </span>
                    </div>
                </ListItem>
            </div>
        );
    }

    const resourceName = props.check.resource ? props.check.resource.name : "";
    const resourceIconName = props.check.resource ? Utilities.getResourceTypeIcons(props.check.resource.type) : "Server"; //Default icon

    const resourceType = props.check.resource ? Utilities.getResourceTypeString(props.check.resource.type) : "";
    const context = useComponentContext();

    return (
        <ListItem key={"approval-item" + props.rowIndex} index={props.rowIndex} details={props.details}>
            <div className="pipelines-approval-row flex-row flex-grow flex-center h-scroll-hidden">
                <Status {...statusIndicatorData.statusProps} size={StatusSize.m} />
                <div className="pipelines-approval-text flex-column h-scroll-hidden">
                    <span className="body-m text-ellipsis">{props.checkName}</span>
                    <span className="body-s text-ellipsis secondary-text">{statusLabel}</span>
                </div>
                <div className="pipeline-resource-group flex-row flex-center">
                    <Icon className="pipelines-resource-icon-1 primary-text" iconName={resourceIconName} />
                    <div className="pipelines-approval-row-text flex-column flex-grow h-scroll-hidden">
                        <Link className="pipelines-resource-link" href={getResourceUrl(context.pageContext, props.check.resource, true)}>
                            {resourceName}
                        </Link>
                        <span className="body-s text-ellipsis secondary-text">{resourceType}</span>
                    </div>
                </div>
            </div>
        </ListItem>
    );
};

interface IStageGroupHeaderProps {
    myApprovalsCount: number;
    myManualValidationsCount: number;
    otherChecksCount: number;
    stageName: string;
    stageTimeoutString: string;
    stageTimeoutStringToolTip: string;
    reviewMode: ReviewMode;
    openReviewDialog: (status: ApprovalStatus) => void;
}

const StageGroupHeader: React.SFC<IStageGroupHeaderProps> = (props) => {
    const shouldShowStage = props.reviewMode === ReviewMode.AllPending && props.stageName && props.stageName !== defaultStageName;
    return (
        <div className="flex-row body-m checks-group-header">
            <div className="flex-column flex-grow justify-end">
                {shouldShowStage && (
                    <>
                        <div className="flex-row font-weight-semibold checks-group-header-row">
                            <Tooltip overflowOnly={true} text={props.stageName}>
                                <span className="text-ellipsis stage-header">{props.stageName}</span>
                            </Tooltip>
                        </div>
                        <Tooltip text={props.stageTimeoutStringToolTip}>
                            <span className="body-m text-ellipsis secondary-text stage-timeout-string">{props.stageTimeoutString}</span>
                        </Tooltip>
                    </>
                )}
                {(props.myApprovalsCount > 0 || props.myManualValidationsCount > 0) && (!shouldShowStage || props.otherChecksCount > 0) && (
                    <div className="flex-row secondary-text checks-group-header-row">
                        <span>{Resources.Waiting}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

interface IChecksGroup {
    headerText: string;
    arePendingChecksPresent: boolean;
    checks: ObservableArray<TaskCheckDetails>;
    onSelectCheck: (taskCheck: TaskCheckDetails, arePendingChecksPresentInStage: boolean) => void;
    hideHeader: boolean;
}

const ChecksGroup: React.SFC<IChecksGroup> = (props) => {
    const onListItemActivate = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<TaskCheckDetails>) => {
        if (!event.defaultPrevented) {
            props.onSelectCheck(listRow.data, props.arePendingChecksPresent);
        }
    };

    const renderChecks = (rowIndex: number, item: TaskCheckDetails, details: IListItemDetails<TaskCheckDetails>) => {
        const status = Utilities.getCheckRunStatusIndicatorData(item.status);
        return (
            <CheckListItem
                check={item}
                details={details}
                rowIndex={rowIndex}
                arePendingChecksPresent={props.arePendingChecksPresent}
                checkName={item.taskCheckConfigSettings.displayName}
                statusLabel={getTaskCheckStatusString(item, status.label, props.arePendingChecksPresent!)}
            />
        );
    };

    return (
        <div>
            {!props.hideHeader && !!props.headerText && showHeader(props.headerText)}
            <List itemProvider={props.checks} renderRow={renderChecks} singleClickActivation={true} width="100%" onActivate={onListItemActivate} />
        </div>
    );
};

function showHeader(headerText: string): JSX.Element {
    return (
        <div className="checks-group-header">
            <div className="body-m secondary-text checks-group-header-row">
                <span>{headerText}</span>
            </div>
        </div>
    );
}

function shouldIncludeApproval(approval: ApprovalDetails): boolean {
    let myUninitiatedApprovalStep = approval.steps.filter(
        (a) => a.status === ApprovalStatus.Uninitiated && a.permissions === ApprovalPermissions.Update
    );
    let myPendingApprovalStep = approval.steps.filter((a) => a.status === ApprovalStatus.Pending && a.permissions === ApprovalPermissions.Update);
    return myUninitiatedApprovalStep.length > 0 && myPendingApprovalStep.length === 0;
}

function getOtherApprovals(approvals: ApprovalDetails[]): ApprovalDetails[] {
    let otherApprovals = approvals.filter((a) => a.permissions === ChecksPermission.View || shouldIncludeApproval(a));
    otherApprovals.sort(Utilities.approvalSort);
    return otherApprovals;
}

function getOtherManualValidations(manualValidations: ManualValidationDetails[]): ManualValidationDetails[] {
    let otherManualValidations = manualValidations.filter((a) => a.permissions === ChecksPermission.View);
    return otherManualValidations;
}

// Checks whether the current user is blocked in one or more approval.
function isCurrentUserBlockedInAnyApproval(approvals: ApprovalDetails[]): boolean {
    const context = useComponentContext();
    const service = context.pageContext.getService<IChecksPanelService>("IChecksPanelService");

    return service.isCurrentUserBlockedFromApproving(approvals);
}

export function getCheckName(check: CheckDetails): string {
    if (equals(check.typeId, Utilities.ApprovalCheckTypeIdString, true)) {
        let approvalCheck = check as ApprovalDetails;
        if(!approvalCheck || !approvalCheck.definitionRef){
            return Resources.Approval;
        }
        
        if(equals(MainApprovalDefTypeId, approvalCheck.definitionRef.id, true)){
            return Resources.Approval;
        }
        if(equals(PreApprovalDefTypeId, approvalCheck.definitionRef.id, true)){
            return Resources.PreApproval;
        }
        if(equals(PostApprovalDefTypeId, approvalCheck.definitionRef.id, true)){
            return Resources.PostApproval;
        }
        return Resources.Approval;
    } else if (equals(check.typeId, Utilities.ExtendsCheckTypeIdString, true)) {
        return Resources.RequiredTemplate;
    } else if (equals(check.typeId, Utilities.ExclusiveLockCheckTypeIdString, true)) {
        return Resources.ExclusiveLock;
    } else if (equals(check.typeId, Utilities.ManualValidationTaskTypeId, true)) {
        return Resources.ManualValidation;
    } else if (equals(check.typeId, Utilities.TaskCheckTypeIdString, true)) {
        let taskCheck = check as TaskCheckDetails;
        return taskCheck !== null ? taskCheck.taskCheckConfigSettings.displayName : "";
    }

    return "";
}

export function getStageTimeoutDetails(stageData: ChecksPanelData) {
    // The timeout for a stage is the minimum timeout value of all the checks present in that stage.
    let minimumTimeout = Number.MAX_SAFE_INTEGER;
    let checkWithMinimumTimeout: CheckDetails = {} as CheckDetails;
    let remainingTimeoutInMinutes = 0;
    let checksCount = 0;
    let stageTimeoutMessage = "";
    let stageTimeoutMessageToolTip = "";

    // If there are no checks that are pending, then that means that the stage has completed and we can skip the timeout message.
    if (!arePendingChecksPresent(stageData)) {
        return {};
    }

    let checksList: CheckDetails[] = [];
    if (stageData.approvals) {
        checksList = checksList.concat(stageData.approvals);
    }

    if (stageData.manualValidations) {
        checksList = checksList.concat(stageData.manualValidations);
    }

    if (stageData.checks) {
        checksList = checksList.concat(stageData.checks);
    }

    // Only pending approvals, manualValdations and checks (except task checks) are taken into account, as completed approvals don't timeout.
    checksList.length > 0 &&
        checksList.map((check) => {
            if (check.status === CheckRunStatus.Running) {
                checksCount++;
                remainingTimeoutInMinutes = Utilities.getRemainingMinutesForCheckTimeout(check.timeout, check.createdDate);
                if (remainingTimeoutInMinutes < minimumTimeout) {
                    minimumTimeout = remainingTimeoutInMinutes;
                    checkWithMinimumTimeout = check;
                }
            }
        });

    // In contrast to other checks,even completed task checks can timeout when the stage is waiting on checks.
    // Evaluate Artifact Policies need a special handling since they don't get timed out once they pass.
    stageData.taskChecks &&
        stageData.taskChecks.map((check) => {
            if (equals(Utilities.EvaluateArtifactPolicyCheckTypeIdString, check.taskCheckConfigSettings.definitionRef.id, true)) {
                if (check.status === CheckRunStatus.Running) {
                    checksCount++;
                    remainingTimeoutInMinutes = Utilities.getRemainingMinutesForCheckTimeout(check.timeout, check.createdDate);
                    if (remainingTimeoutInMinutes < minimumTimeout) {
                        minimumTimeout = remainingTimeoutInMinutes;
                        checkWithMinimumTimeout = check;
                    }
                }
            } else if (check.status === CheckRunStatus.Running || check.status === CheckRunStatus.Approved) {
                checksCount++;
                remainingTimeoutInMinutes = Utilities.getRemainingMinutesForCheckTimeout(check.timeout, check.createdDate);
                if (remainingTimeoutInMinutes < minimumTimeout) {
                    minimumTimeout = remainingTimeoutInMinutes;
                    checkWithMinimumTimeout = check;
                }
            }
        });

    if (minimumTimeout === Number.MAX_SAFE_INTEGER) {
        return {};
    }

    stageTimeoutMessage = Utilities.getCheckTimeoutOrReevaluationString(Resources.Timeout, minimumTimeout);
    stageTimeoutMessageToolTip =
        checksCount === 1
            ? format(Resources.StageTimeoutMessageToolTipWithSingleCheck, getCheckName(checkWithMinimumTimeout))
            : format(Resources.StageTimeoutMessageToolTipWithMultipleChecks, getCheckName(checkWithMinimumTimeout), checksCount - 1);

    return { stageTimeoutMessage, stageTimeoutMessageToolTip };
}

export function getTaskCheckStatusString(check: CheckDetails, statusLabel: string, arePendingChecksPresent: boolean): string {
    // Only for task checks except artifact policies the format is {CheckStatus} . {Reevaluate in X minutes}

    let checkStatusString = statusLabel;
    if (equals(check.typeId, Utilities.TaskCheckTypeIdString, true)) {
        let taskCheck = check as TaskCheckDetails;
        if (taskCheck !== null) {
            if (
                !equals(taskCheck.taskCheckConfigSettings.definitionRef.id, Utilities.EvaluateArtifactPolicyCheckTypeIdString, true) &&
                taskCheck.taskCheckConfigSettings &&
                taskCheck.taskCheckConfigSettings.retryInterval > 0 &&
                (check.status === CheckRunStatus.Approved || check.status === CheckRunStatus.Running)
            ) {
                let attemptToLogIdMap = taskCheck.taskCheckData.attemptToTaskCheckTimelineRecordDataMap;
                let attemptsList = attemptToLogIdMap ? Object.keys(attemptToLogIdMap) : [];

                if (attemptsList.length > 0 && arePendingChecksPresent) {
                    const latestAttempt = attemptsList.sort()[attemptsList.length - 1];
                    const latestAttemptFinishTime = attemptToLogIdMap[parseInt(latestAttempt)].finishTime;
                    if (latestAttemptFinishTime) {
                        const now = new Date();
                        const reevaluationTime =
                            latestAttemptFinishTime.getTime() + taskCheck.taskCheckConfigSettings.retryInterval * 60000 - now.getTime();
                        checkStatusString =
                            reevaluationTime >= 0
                                ? format(
                                      Resources.CheckReevaluateStatusString,
                                      statusLabel,
                                      Utilities.getCheckTimeoutOrReevaluationString(Resources.RetryString, reevaluationTime)
                                  )
                                : statusLabel;
                    }
                }
            }
        }
    }

    return checkStatusString;
}

export function arePendingChecksPresent(stageData: ChecksPanelData): boolean {
    return (
        (stageData.approvals && stageData.approvals.some((approval) => approval.status === ApprovalStatus.Pending)) ||
        (stageData.manualValidations && stageData.manualValidations.some((manualValidation) => manualValidation.status === ApprovalStatus.Pending)) ||
        (stageData.checks && stageData.checks.some((check) => check.status === CheckRunStatus.Running)) ||
        (stageData.taskChecks && stageData.taskChecks.some((taskCheck) => taskCheck.status === CheckRunStatus.Running))
    );
}

export function shouldBypassChecksPanel(checkStages: ChecksPanelData[], permissionStages: ChecksPanelData[], hasNonApprovalChecks: boolean): boolean {
    let bypassChecksPanel = false;
    if (checkStages.length === 1 && permissionStages.length === 0 && !hasNonApprovalChecks) {
        //Bypass if a stage has only one check on a whole.
        if (checkStages[0].approvals && checkStages[0].taskChecks) {
            bypassChecksPanel = false;
        } else if (checkStages[0].approvals && checkStages[0].approvals.length === 1) {
            //BUG 1982647, users were not able to view nor approve/reject manual validation checks when approvals were present
            if (checkStages[0].manualValidations && checkStages[0].manualValidations.length === 0) {
                bypassChecksPanel = true;
            }
        } else if (checkStages[0].taskChecks && checkStages[0].taskChecks.length === 1) {
            bypassChecksPanel = true;
        } else if (checkStages[0].manualValidations && checkStages[0].manualValidations.length === 1) {
            bypassChecksPanel = true;
        }
    }
    return bypassChecksPanel;
}

Components.add("ms.tfs-build.approvals-list-panel", ChecksPanel);
